import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, MapPin, Mail, Phone, User, Ticket, ArrowRight, Copy, Clock } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { publicApi } from '@services/publicApi';
import { formatDate, formatDateTime, formatCurrency, copyToClipboard } from '@utils';
import toast from 'react-hot-toast';

export default function OrderConfirmationPage() {
  const { reference } = useParams<{ reference: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['order-confirmation', reference],
    queryFn: () => publicApi.getOrderStatus(reference!),
    enabled: !!reference,
  });

  const order = data?.data;

  useEffect(() => {
    if (order) {
      document.title = `Order ${order.orderNumber} - Confirmed`;
    }
  }, [order]);

  const handleCopyOrderRef = () => {
    if (order) {
      copyToClipboard(order.orderReference).then(ok => {
        if (ok) toast.success('Order reference copied!');
      });
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
        <p className="text-gray-500 mt-4">Loading your order...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Order Not Found</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6">We couldn't find this order. Please check the reference.</p>
        <Link to="/events" className="text-primary-600 hover:text-primary-700 font-medium">
          Browse Events
        </Link>
      </div>
    );
  }

  const isPaid = order.paymentStatus === 'paid';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
    >
      {/* Success Header */}
      {isPaid && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="text-center mb-10"
        >
          <div className="w-20 h-20 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-5">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Order Confirmed!</h1>
          <p className="text-gray-500 dark:text-gray-400">
            A confirmation email has been sent to <span className="font-medium text-gray-700 dark:text-gray-300">{order.buyerEmail}</span>
          </p>
        </motion.div>
      )}

      {!isPaid && (
        <div className="text-center mb-10">
          <div className="w-20 h-20 mx-auto rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mb-5">
            <Clock className="w-10 h-10 text-yellow-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Order Pending</h1>
          <p className="text-gray-500 dark:text-gray-400">
            Your order is awaiting payment confirmation.
          </p>
        </div>
      )}

      {/* Order Details Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden mb-6"
      >
        {/* Order Reference Bar */}
        <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Order Reference</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white font-mono">{order.orderReference}</p>
          </div>
          <button
            onClick={handleCopyOrderRef}
            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            title="Copy reference"
          >
            <Copy className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Event Info */}
          {order.event && (
            <div className="flex items-start gap-4">
              {order.event.featuredImage ? (
                <img src={order.event.featuredImage} alt={order.event.name} className="w-16 h-16 rounded-xl object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <Calendar className="w-7 h-7 text-primary-500" />
                </div>
              )}
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white">{order.event.name}</h3>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 space-y-0.5">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDateTime(order.event.startDate)}
                  </div>
                  {order.event.venue && (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" />
                      {order.event.venue.name}, {order.event.venue.city}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Buyer Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <User className="w-4 h-4" />
              {order.buyerName}
            </div>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <Mail className="w-4 h-4" />
              {order.buyerEmail}
            </div>
            {order.buyerPhone && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Phone className="w-4 h-4" />
                {order.buyerPhone}
              </div>
            )}
          </div>

          {/* Order Items */}
          <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
            <div className="space-y-2">
              {order.items?.map(item => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    {item.quantity}x {item.itemName}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(item.totalAmount, item.currency)}
                  </span>
                </div>
              ))}
            </div>

            {order.discountAmount > 0 && (
              <div className="flex justify-between text-sm mt-2 text-green-600">
                <span>Discount {order.couponCode ? `(${order.couponCode})` : ''}</span>
                <span>-{formatCurrency(order.discountAmount, order.currency)}</span>
              </div>
            )}

            <div className="flex justify-between font-bold text-lg mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <span className="text-gray-900 dark:text-white">Total Paid</span>
              <span className="text-gray-900 dark:text-white">{formatCurrency(order.totalAmount, order.currency)}</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Tickets with QR Codes */}
      {isPaid && order.tickets && order.tickets.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="space-y-4 mb-8"
        >
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Ticket className="w-5 h-5" /> Your Tickets
          </h2>

          {order.tickets.map((ticket, i) => (
            <motion.div
              key={ticket.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 + i * 0.05 }}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-5 flex flex-col sm:flex-row items-center gap-5"
            >
              {/* QR Code */}
              <div className="flex-shrink-0 bg-white p-3 rounded-xl border border-gray-100">
                <QRCodeSVG
                  value={ticket.ticketUuid}
                  size={120}
                  level="M"
                  includeMargin={false}
                />
              </div>

              {/* Ticket Info */}
              <div className="flex-1 text-center sm:text-left">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Ticket #{i + 1}</p>
                <p className="font-bold text-gray-900 dark:text-white mb-1">{ticket.ticketType?.name || 'Ticket'}</p>
                <p className="text-sm font-mono text-gray-500 dark:text-gray-400 mb-2">{ticket.ticketNumber}</p>
                <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  {ticket.status === 'active' ? 'Valid' : ticket.status}
                </div>
              </div>

              {/* Price */}
              <div className="text-right">
                <p className="font-bold text-gray-900 dark:text-white">
                  {ticket.pricePaid === 0 ? 'Free' : formatCurrency(ticket.pricePaid, ticket.currency)}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/events"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
        >
          Browse More Events <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.div>
  );
}
